import { DialogContent } from '@mui/material'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import Fade from '@mui/material/Fade'
import IconButton from '@mui/material/IconButton'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import CloseIcon from '@mui/icons-material/Close'
import _ from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { isWebpSupported } from 'react-image-webp/modules/utils'
import Loader from '@components/Loader'
import { checkVariantAvailability, matchVariantFromSelection } from '../../../utils/misc'
import ProductVariantPrice from '../ProductVariantPrice'
import ProductVariantsPicker from '../ProductVariantsPicker'
import ProductFormEditButtons from './ProductFormEditButtons'
import ProductImagesSwiper from './ProductImagesSwiper'
import { useTranslation } from 'react-i18next'

interface IProps {
  isOpen: boolean

  onClosePanel(): void

  pickerDataJson: any
  loading?: boolean

  updateBag(pk: number, quantity: number, selectedVariant: any): void

  addToBagFullKit?(selectedVariant: any): void

  customizeKit?(selectedVariant: any): void

  add?: boolean

  onOpenPanel(): void

  qty?: number
  selectedVariantPk?: number
}

const ProductRightPanel: React.FC<IProps> = ({
  isOpen,
  onClosePanel,
  pickerDataJson,
  loading,
  updateBag,
  addToBagFullKit,
  customizeKit,
  add = false,
  onOpenPanel,
  qty,
  selectedVariantPk,
}) => {
  const { t } = useTranslation()
  const [selectedVariant, setSelectedVariant] = useState(null)
  const [quantity, setQuantity] = useState(qty || 1)
  const [selectedValues, setSelectedValues] = useState({})

  const attributes = useMemo(() => {
    const properties = pickerDataJson?.product?.variants.map(variant => variant.properties).flat()
    const attributes = {}
    properties?.forEach(prop => {
      attributes[prop.attributeName] = attributes[prop.attributeName] || []
      attributes[prop.attributeName].push(prop.valueName)
    })
    properties?.forEach(prop => {
      attributes[prop.attributeName] = [...new Set(attributes[prop.attributeName])]
    })
    return attributes
  }, [pickerDataJson])

  useEffect(() => {
    if (pickerDataJson) {
      const product = pickerDataJson.product
      const variants = product?.variants
      const selectedAttrs = {}
      if (add) {
        if (variants.length > 1) {
          setSelectedVariant(null)
        } else {
          setSelectedVariant(variants[0])
        }
        setQuantity(1)
        Object.entries(attributes).map(([key, values]) => {
          const attrValues: any = values
          if (attrValues.length === 1) {
            selectedAttrs[key] = values[0]
          }
        })
      } else {
        const variantsInCart = selectedVariantPk
          ? variants.filter(el => el.pk === selectedVariantPk)
          : variants.filter(el => el.isInCart)
        const cartVariant = variantsInCart[0] || variants[0]
        cartVariant.properties.map(prop => {
          selectedAttrs[prop.attributeName] = prop.valueName
        })
        setSelectedVariant(cartVariant)
        setQuantity(qty || 1)
      }
      setSelectedValues(selectedAttrs)
    }
  }, [pickerDataJson])
  const handleMatchVariantFromSelection = selection => {
    return matchVariantFromSelection(selection, pickerDataJson?.product.variants)
  }
  const onChangeSelectedVariant = attrs => {
    const matchedVariant = handleMatchVariantFromSelection(attrs)
    setSelectedVariant(matchedVariant)
    setQuantity(1)
  }
  const onChangeQuantity = val => {
    if (parseInt(val) > 0) {
      setQuantity(parseInt(val))
    }
  }

  const onUpdateBag = e => {
    e.preventDefault()
    updateBag(selectedVariant.pk, quantity, selectedVariant)
  }

  const onAddToBagFullKit = e => {
    e.preventDefault()
    if (addToBagFullKit) addToBagFullKit(selectedVariant)
  }
  const onCustomizeKit = e => {
    e.preventDefault()
    if (customizeKit) customizeKit(selectedVariant)
  }

  const productName = pickerDataJson?.name

  const reflectionAllImages = useMemo(() => {
    const variantsImages = pickerDataJson?.product?.variants
      .map(variant => variant.images.edges)
      .flat()
      .map(edge => edge.node)
    const productImages = pickerDataJson?.product?.images || []
    const images = productImages.filter(img => variantsImages.map(node => node.id).includes(img.id))
    return (
      _.uniqBy(
        pickerDataJson?.product?.hasVariants && !!variantsImages?.length ? images : productImages,
        (e: any) => e.id
      ) || []
    )
  }, [pickerDataJson])

  const images =
    pickerDataJson?.product?.hasVariants && !!selectedVariant?.images.edges.length
      ? selectedVariant?.images.edges.map(edge => edge.node) || reflectionAllImages
      : reflectionAllImages

  return (
    <SwipeableDrawer anchor='right' open={isOpen} onClose={onClosePanel} onOpen={onOpenPanel} disableSwipeToOpen>
      <div className='flex flex-col h-full'>
        <DialogTitle>
          <span className='text-capitalize font-semibold text-left block'>
            {add ? t('title.quickView', 'Quick view') : t('title.editItem', 'Edit item')}
          </span>
          <span className='absolute right-10 top-10'>
            <IconButton onClick={onClosePanel} size='medium' title={t('close', 'Close')}>
              <CloseIcon fontSize='inherit' />
            </IconButton>
          </span>
        </DialogTitle>

        <DialogContent className='w-400 max-w-full' dividers>
          <Loader className='spinner z-10' type='ThreeDots' color='#333' visible={loading} height={100} width={100} />
          <Fade in={true} timeout={1000}>
            <div>
              <h1 className='head-title' itemProp='name'>
                {productName}
              </h1>
              {pickerDataJson && (
                <ProductVariantPrice
                  selectedVariant={selectedVariant}
                  quantity={quantity}
                  product={pickerDataJson.product}
                  checkVariantAvailability={() => checkVariantAvailability(selectedVariant)}
                />
              )}
              <ProductImagesSwiper
                images={
                  images?.map(img => ({
                    src: isWebpSupported() ? img.thumbnailUrlWebp : img.thumbnailUrl,
                    alt: `${productName}-${img.pk}`,
                  })) || []
                }
              />
            </div>
          </Fade>
        </DialogContent>
        <DialogActions>
          <div className='w-full px-16'>
            <Fade in={true} timeout={1000}>
              <div>
                {pickerDataJson && (
                  <div>
                    <ProductVariantsPicker
                      attributes={attributes}
                      checkVariantAvailability={() => checkVariantAvailability(selectedVariant)}
                      selectedValues={selectedValues}
                      setSelectedValues={setSelectedValues}
                      onChange={onChangeSelectedVariant}
                      matchVariantFromSelection={handleMatchVariantFromSelection}
                    />
                    {selectedVariant &&
                      !selectedVariant.isSupply &&
                      selectedVariant.shippingRequired &&
                      checkVariantAvailability(selectedVariant) && (
                        <label className='quantity-select__label control-label text-bold'>
                          {t('quantity', 'Quantity')}
                        </label>
                      )}
                    <ProductFormEditButtons
                      selectedVariant={selectedVariant}
                      quantity={quantity}
                      onChangeQuantity={onChangeQuantity}
                      checkVariantAvailability={() => checkVariantAvailability(selectedVariant)}
                      onUpdateBag={onUpdateBag}
                      onAddToBagFullKit={onAddToBagFullKit}
                      onCustomizeKit={onCustomizeKit}
                      add={add}
                    />
                  </div>
                )}
              </div>
            </Fade>
          </div>
        </DialogActions>
      </div>
    </SwipeableDrawer>
  )
}

export default ProductRightPanel
